import { useState, useRef, useEffect, useCallback } from 'react'
import { Box, Flex, HStack, Text, IconButton, Tooltip, Spinner } from '@chakra-ui/react'
import { Play, Pause, DownloadSimple, ArrowClockwise, WarningCircle } from '@phosphor-icons/react'

interface AudioPlayerProps {
  src: string
  isOwn?: boolean
  showDownload?: boolean
}

const formatDuration = (seconds: number) => {
  if (isNaN(seconds) || !isFinite(seconds)) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const PLAYBACK_SPEEDS = [1, 1.25, 1.5, 1.75, 2]
const MAX_RETRIES = 3

export function AudioPlayer({ src, isOwn = false, showDownload = true }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const progressBarRef = useRef<HTMLDivElement>(null)
  const retryCountRef = useRef(0)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const blobUrlRef = useRef<string | null>(null)

  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [playbackSpeed, setPlaybackSpeed] = useState(1)
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'retrying' | 'failed'>('loading')
  const [waveformBars] = useState(() =>
    Array.from({ length: 35 }, () => Math.random() * 0.6 + 0.4)
  )

  const bgColor = isOwn ? 'yellow.500' : 'gray.700'
  const textColor = isOwn ? 'gray.900' : 'white'
  const buttonBg = isOwn ? 'blackAlpha.200' : 'gray.600'
  const buttonHover = isOwn ? 'blackAlpha.300' : 'gray.500'
  const wavePlayedColor = isOwn ? 'gray.800' : 'yellow.400'
  const waveUnplayedColor = isOwn ? 'blackAlpha.400' : 'gray.500'
  const timeColor = isOwn ? 'gray.700' : 'gray.400'

  const tryLoadViaBlob = useCallback(async () => {
    if (!src || src.startsWith('blob:')) return false
    try {
      console.log('[AudioPlayer] Tentando carregar via fetch blob:', src)
      const response = await fetch(src, { mode: 'cors', credentials: 'omit' })
      if (!response.ok) return false
      let blob = await response.blob()
      const headerType = response.headers.get('content-type')?.split(';')[0]?.trim()
      if ((!blob.type || blob.type === 'application/octet-stream') && headerType?.startsWith('audio/')) {
        blob = new Blob([blob], { type: headerType })
      }
      const audioBlob = blob
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current)
      const blobUrl = URL.createObjectURL(audioBlob)
      blobUrlRef.current = blobUrl
      if (audioRef.current) {
        audioRef.current.src = blobUrl
        audioRef.current.load()
      }
      return true
    } catch (err) {
      console.error('[AudioPlayer] Fetch blob falhou:', err)
      return false
    }
  }, [src])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    let seekedForDuration = false
    let triedBlob = false

    const updateDuration = () => {
      if (audio.duration && isFinite(audio.duration) && audio.duration !== Infinity) {
        setDuration(audio.duration)
      }
    }

    const handleLoadedMetadata = () => {
      if (audio.duration && isFinite(audio.duration) && audio.duration !== Infinity) {
        setDuration(audio.duration)
      } else {
        seekedForDuration = true
        audio.currentTime = 1e101
      }
    }

    const handleDurationChange = () => {
      if (audio.duration && isFinite(audio.duration) && audio.duration !== Infinity) {
        setDuration(audio.duration)
        if (seekedForDuration) {
          seekedForDuration = false
          audio.currentTime = 0
        }
      }
    }

    const handleTimeUpdate = () => {
      if (seekedForDuration) {
        if (audio.duration && isFinite(audio.duration) && audio.duration !== Infinity) {
          setDuration(audio.duration)
          seekedForDuration = false
          audio.currentTime = 0
        }
        return
      }
      setCurrentTime(audio.currentTime)
      updateDuration()
    }

    const handleEnded = () => setIsPlaying(false)
    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)
    const handleCanPlay = () => {
      updateDuration()
      setLoadState('ready')
      retryCountRef.current = 0
    }

    // Alguns navegadores/arquivos não disparam canplay; loadedmetadata/loadeddata evita UI presa em "carregando"
    const handleLoadedData = () => {
      updateDuration()
      setLoadState('ready')
      retryCountRef.current = 0
    }

    const handleError = async () => {
      if (!src || src.startsWith('blob:')) {
        if (audio.src.startsWith('blob:')) {
          console.error('[AudioPlayer] Áudio corrompido ou formato não suportado:', src)
          setLoadState('failed')
        }
        return
      }

      const errorCode = audio.error?.code
      const errorMsg = audio.error?.message || 'unknown'
      console.warn(`[AudioPlayer] Erro ao carregar áudio (tentativa ${retryCountRef.current + 1}/${MAX_RETRIES}):`, {
        src: src.substring(0, 80),
        errorCode,
        errorMsg,
      })

      if (!triedBlob) {
        triedBlob = true
        setLoadState('retrying')
        const success = await tryLoadViaBlob()
        if (success) return
      }

      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current += 1
        const delay = Math.min(1000 * Math.pow(2, retryCountRef.current - 1), 8000)
        setLoadState('retrying')
        retryTimerRef.current = setTimeout(() => {
          if (audioRef.current) {
            audioRef.current.src = src
            audioRef.current.load()
          }
        }, delay)
      } else {
        console.error('[AudioPlayer] Falha permanente ao carregar áudio após todas as tentativas:', src)
        setLoadState('failed')
      }
    }

    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('durationchange', handleDurationChange)
    audio.addEventListener('loadeddata', handleLoadedData)
    audio.addEventListener('canplay', handleCanPlay)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('play', handlePlay)
    audio.addEventListener('pause', handlePause)
    audio.addEventListener('error', handleError)

    if (audio.duration && isFinite(audio.duration) && audio.duration !== Infinity) {
      setDuration(audio.duration)
    }

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('durationchange', handleDurationChange)
      audio.removeEventListener('loadeddata', handleLoadedData)
      audio.removeEventListener('canplay', handleCanPlay)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('play', handlePlay)
      audio.removeEventListener('pause', handlePause)
      audio.removeEventListener('error', handleError)
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current)
    }
  }, [src, tryLoadViaBlob])

  useEffect(() => {
    retryCountRef.current = 0
    setLoadState('loading')
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
        blobUrlRef.current = null
      }
    }
  }, [src])

  const togglePlay = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      audio.pause()
    } else {
      audio.play().catch(console.error)
    }
  }, [isPlaying])

  const handleRetry = useCallback(() => {
    retryCountRef.current = 0
    setLoadState('loading')
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = null
    }
    if (audioRef.current) {
      audioRef.current.src = src
      audioRef.current.load()
    }
  }, [src])

  const cycleSpeed = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return

    const currentIndex = PLAYBACK_SPEEDS.indexOf(playbackSpeed)
    const nextIndex = (currentIndex + 1) % PLAYBACK_SPEEDS.length
    const newSpeed = PLAYBACK_SPEEDS[nextIndex]

    audio.playbackRate = newSpeed
    setPlaybackSpeed(newSpeed)
  }, [playbackSpeed])

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current
    const progressBar = progressBarRef.current
    if (!audio || !progressBar) return

    const rect = progressBar.getBoundingClientRect()
    const clickPosition = (e.clientX - rect.left) / rect.width
    audio.currentTime = clickPosition * duration
  }, [duration])

  const handleDownload = useCallback(() => {
    const link = document.createElement('a')
    link.href = src
    link.download = src.split('/').pop() || 'audio.ogg'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [src])

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  if (loadState === 'failed') {
    return (
      <Flex
        bg={bgColor}
        borderRadius="2xl"
        px={3}
        py={2}
        minW="200px"
        maxW="300px"
        align="center"
        gap={2}
      >
        <audio ref={audioRef} preload="metadata" />
        <IconButton
          aria-label="Tentar novamente"
          icon={<ArrowClockwise size={20} />}
          onClick={handleRetry}
          size="sm"
          borderRadius="full"
          bg={buttonBg}
          color={textColor}
          _hover={{ bg: buttonHover }}
          minW={8}
          h={8}
        />
        <Box flex={1}>
          <HStack spacing={1}>
            <WarningCircle size={14} color={isOwn ? '#4A5568' : '#A0AEC0'} />
            <Text fontSize="xs" color={timeColor}>
              Áudio indisponível
            </Text>
          </HStack>
          <Text fontSize="10px" color={timeColor} opacity={0.7}>
            Formato não suportado ou arquivo corrompido
          </Text>
        </Box>
        {showDownload && (
          <Tooltip label="Tentar baixar">
            <IconButton
              aria-label="Baixar áudio"
              icon={<DownloadSimple size={16} />}
              onClick={handleDownload}
              size="xs"
              borderRadius="full"
              bg={buttonBg}
              color={textColor}
              _hover={{ bg: buttonHover }}
            />
          </Tooltip>
        )}
      </Flex>
    )
  }

  return (
    <Flex
      bg={bgColor}
      borderRadius="2xl"
      px={3}
      py={2}
      minW="200px"
      maxW="300px"
      align="center"
      gap={2}
    >
      <audio ref={audioRef} src={src} preload="metadata" />

      <IconButton
        aria-label={loadState === 'retrying' ? 'Carregando...' : isPlaying ? 'Pausar' : 'Reproduzir'}
        icon={
          loadState === 'retrying'
            ? <Spinner size="sm" />
            : isPlaying ? <Pause size={20} weight="fill" /> : <Play size={20} weight="fill" />
        }
        onClick={togglePlay}
        isDisabled={loadState === 'retrying'}
        size="sm"
        borderRadius="full"
        bg={buttonBg}
        color={textColor}
        _hover={{ bg: buttonHover }}
        minW={8}
        h={8}
      />

      <Box flex={1} position="relative">
        <Box
          ref={progressBarRef}
          onClick={handleProgressClick}
          cursor="pointer"
          position="relative"
          h="32px"
          display="flex"
          alignItems="center"
        >
          <HStack spacing="1px" w="100%" h="100%" align="center" justify="start">
            {waveformBars.map((height, i) => {
              const barProgress = (i / waveformBars.length) * 100
              const isPlayed = barProgress < progress

              return (
                <Box
                  key={i}
                  w="3px"
                  h={`${height * 24}px`}
                  bg={isPlayed ? wavePlayedColor : waveUnplayedColor}
                  borderRadius="full"
                  transition="background 0.1s"
                />
              )
            })}
          </HStack>
        </Box>

        <Text fontSize="xs" color={timeColor} mt={0}>
          {loadState === 'retrying'
            ? 'Carregando...'
            : `${formatDuration(currentTime)} / ${formatDuration(duration)}`
          }
        </Text>
      </Box>

      <Tooltip label="Velocidade">
        <Box
          as="button"
          onClick={cycleSpeed}
          bg={buttonBg}
          color={textColor}
          fontSize="xs"
          fontWeight="bold"
          px={2}
          py={1}
          borderRadius="full"
          minW="36px"
          textAlign="center"
          _hover={{ bg: buttonHover }}
          transition="background 0.2s"
        >
          {playbackSpeed}x
        </Box>
      </Tooltip>

      {showDownload && (
        <Tooltip label="Baixar áudio">
          <IconButton
            aria-label="Baixar áudio"
            icon={<DownloadSimple size={16} />}
            onClick={handleDownload}
            size="xs"
            borderRadius="full"
            bg={buttonBg}
            color={textColor}
            _hover={{ bg: buttonHover }}
          />
        </Tooltip>
      )}
    </Flex>
  )
}

export default AudioPlayer
